import * as THREE from 'three';
import { medalsForDistance, type MedalThresholds, type PhaseId } from '../progress/model';
import type { SurfaceId } from '../physics/surfaces';
import { disposeObject } from '../render/dispose';
import { makeAsphaltTexture, makeCheckerTexture } from '../render/textures';
import { CART } from '../cart/CartModel';
import { layoutNitroPads } from './nitroLayout';
import { buildSpline, sampleSpline, type SplineFrame } from './spline';
import type { BuiltTrack, CheckpointMarker, TrackAtmosphere, TrackContext } from './types';

export interface SurfaceBand {
  /** 0..1 along the lap. Wraps when start > end. */
  start: number;
  end: number;
  surface: SurfaceId;
}

export interface CircuitOptions {
  id: PhaseId;
  points: THREE.Vector3[];
  width: number;
  surfaceId: SurfaceId;
  samplesPerSegment?: number;
  checkpointEvery?: number;
  maxBank?: number;
  barrierHeight?: number;
  roadColor?: number;
  lineColor?: number;
  barrierColor?: number;
  atmosphere: TrackAtmosphere;
  propulsion?: boolean;
  dust?: boolean;
  medalPace?: MedalThresholds;
  laps: number;
  bands?: SurfaceBand[];
}

let asphaltTexture: THREE.CanvasTexture | null = null;
let checkerTexture: THREE.CanvasTexture | null = null;

function sharedAsphalt(): THREE.CanvasTexture {
  asphaltTexture ??= makeAsphaltTexture();
  return asphaltTexture;
}

function sharedChecker(): THREE.CanvasTexture {
  checkerTexture ??= makeCheckerTexture();
  return checkerTexture;
}

export function buildCircuit(options: CircuitOptions, ctx: TrackContext): BuiltTrack {
  const samples = options.samplesPerSegment ?? 8;
  const { frames, length } = buildSpline(options.points, samples, options.maxBank ?? 0.12);
  if (import.meta.env.DEV) warnIfPinched(frames, options.width);

  const group = new THREE.Group();
  group.name = options.id;
  const blockers = new THREE.Group();
  blockers.name = 'blockers';
  group.add(blockers);

  const road = roadGeometry(frames, length, options.width);
  const roadMaterial = new THREE.MeshStandardMaterial({
    map: sharedAsphalt(),
    color: options.roadColor ?? 0xffffff,
    roughness: 0.92,
    metalness: 0.04,
  });
  const roadMesh = new THREE.Mesh(road, roadMaterial);
  roadMesh.receiveShadow = true;
  roadMesh.name = 'road';
  group.add(roadMesh);

  const lineMaterial = new THREE.MeshStandardMaterial({
    color: options.lineColor ?? 0xf2f2f2,
    roughness: 0.8,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
  const edge = options.width * 0.5 - 0.35;
  group.add(new THREE.Mesh(ribbon(frames, length, -edge, 0.08, 0.025), lineMaterial));
  group.add(new THREE.Mesh(ribbon(frames, length, edge, 0.08, 0.025), lineMaterial));
  const dashMaterial = lineMaterial.clone();
  group.add(new THREE.Mesh(dashedRibbon(frames, length, 0, 0.1, 0.03, 4.5, 3.2), dashMaterial));

  const concrete = new THREE.MeshStandardMaterial({ color: 0xb7b1a6, roughness: 0.95 });
  const sidewalkWidth = 1.5;
  const sideGeoL = ribbon(frames, length, -(options.width * 0.5 + sidewalkWidth * 0.5), sidewalkWidth, 0.08);
  const sideGeoR = ribbon(frames, length, options.width * 0.5 + sidewalkWidth * 0.5, sidewalkWidth, 0.08);
  const leftWalk = new THREE.Mesh(sideGeoL, concrete);
  const rightWalk = new THREE.Mesh(sideGeoR, concrete);
  leftWalk.receiveShadow = true;
  rightWalk.receiveShadow = true;
  group.add(leftWalk, rightWalk);

  const barrierHeight = options.barrierHeight ?? 0.95;
  const barrierMat = new THREE.MeshStandardMaterial({
    color: options.barrierColor ?? 0xd9d3c5,
    roughness: 0.78,
    metalness: options.barrierColor == null ? 0.05 : 0.45,
  });
  const stripeMat = new THREE.MeshStandardMaterial({ color: 0xe10600, roughness: 0.6 });
  const barrierGeo = new THREE.BoxGeometry(1, 1, 1);
  const barriers = new THREE.InstancedMesh(barrierGeo, barrierMat, frames.length * 2);
  const stripes = new THREE.InstancedMesh(barrierGeo.clone(), stripeMat, frames.length * 2);
  barriers.castShadow = ctx.quality === 'high';
  barriers.receiveShadow = true;
  const dummy = new THREE.Object3D();
  let barrierCount = 0;

  const quat = new THREE.Quaternion();
  const basis = new THREE.Matrix4();

  const chord = new THREE.Vector3();
  const up = new THREE.Vector3();
  const sideAxis = new THREE.Vector3();
  for (let i = 0; i < frames.length; i += 1) {
    const frame = frames[i];
    const next = frames[(i + 1) % frames.length];
    chord.subVectors(next.position, frame.position);
    const spanLen = chord.length();
    if (spanLen < 0.2) continue;
    chord.multiplyScalar(1 / spanLen);
    up.copy(frame.normal).add(next.normal);
    up.addScaledVector(chord, -up.dot(chord));
    if (up.lengthSq() < 1e-8) up.copy(frame.normal);
    if (up.lengthSq() < 1e-8) up.set(0, 1, 0);
    up.normalize();
    sideAxis.crossVectors(up, chord);
    if (sideAxis.lengthSq() < 1e-8) sideAxis.copy(frame.binormal);
    sideAxis.normalize();
    up.crossVectors(chord, sideAxis).normalize();
    basis.makeBasis(sideAxis, up, chord);
    quat.setFromRotationMatrix(basis);
    const rotation = { x: quat.x, y: quat.y, z: quat.z, w: quat.w };
    const halfHeight = 0.18;
    const mid = frame.position.clone().lerp(next.position, 0.5);
    // Sensor, sunk below the spline: slope seams stay queryable and cannot stop the cart.
    const center = mid.clone().addScaledVector(up, -(0.42 + halfHeight));
    const halfLen = spanLen * 0.49;

    ctx.addCollider(
      ctx.rapier.ColliderDesc.cuboid(options.width * 0.5, halfHeight, halfLen)
        .setTranslation(center.x, center.y, center.z)
        .setRotation(rotation)
        .setFriction(0)
        .setRestitution(0)
        .setSensor(true),
      { kind: 'road', surface: surfaceAlong(frame.distance, length, options.surfaceId, options.bands) },
    );

    const barrierOffset = options.width * 0.5 + sidewalkWidth + 0.28;
    for (const side of [-1, 1]) {
      const pos = mid.clone()
        .addScaledVector(sideAxis, side * barrierOffset)
        .addScaledVector(up, barrierHeight * 0.5);
      ctx.addCollider(
        ctx.rapier.ColliderDesc.cuboid(0.28, barrierHeight * 0.5, halfLen * 0.96)
          .setTranslation(pos.x, pos.y, pos.z)
          .setRotation(rotation)
          .setFriction(0.4)
          .setRestitution(0.05),
        { kind: 'barrier', surface: 'concrete' },
      );
      dummy.position.copy(pos);
      dummy.quaternion.copy(quat);
      dummy.scale.set(0.5, barrierHeight, Math.max(0.4, spanLen * 0.96));
      dummy.updateMatrix();
      barriers.setMatrixAt(barrierCount, dummy.matrix);
      dummy.position.addScaledVector(up, barrierHeight * 0.18);
      dummy.scale.set(0.52, 0.12, Math.max(0.4, spanLen * 0.96));
      dummy.updateMatrix();
      stripes.setMatrixAt(barrierCount, dummy.matrix);
      barrierCount += 1;
    }
  }
  barriers.count = barrierCount;
  stripes.count = barrierCount;
  barriers.instanceMatrix.needsUpdate = true;
  stripes.instanceMatrix.needsUpdate = true;
  group.add(barriers, stripes);
  blockers.add(barriers);

  const ground = ctx.addCollider(
    ctx.rapier.ColliderDesc.cuboid(900, 0.4, 900)
      .setTranslation(0, -1.3, 0)
      .setFriction(0.9),
    { kind: 'ground', surface: 'offroad' },
  );
  void ground;

  const padSpecs = layoutNitroPads(length);
  const checkpoints: CheckpointMarker[] = [];
  padSpecs.forEach((spec, i) => {
    const distance = spec.distance;
    const sample = sampleSpline(frames, distance, length);
    const position = sample.position.clone().addScaledVector(sample.normal, 1.15);
    const halfLength = 1.65;
    const collider = ctx.addCollider(
      ctx.rapier.ColliderDesc.cuboid(options.width * 0.52, 1.5, halfLength)
        .setTranslation(position.x, position.y, position.z)
        .setRotation({
          x: sample.quaternion.x,
          y: sample.quaternion.y,
          z: sample.quaternion.z,
          w: sample.quaternion.w,
        })
        .setSensor(true),
      { kind: 'checkpoint', surface: options.surfaceId, checkpointIndex: i },
    );
    const padObject = addNitroPad(group, sample, options.width, spec.pad);
    checkpoints.push({
      index: i,
      distance,
      position: sample.position.clone().addScaledVector(sample.normal, CART.rideHeight),
      quaternion: sample.quaternion.clone(),
      collider,
      pad: spec.pad,
      padObject,
    });
  });

  const spawnSample = sampleSpline(frames, 8, length);
  const finish = sampleSpline(frames, 0, length);
  const finishMesh = new THREE.Mesh(
    new THREE.PlaneGeometry(options.width * 0.92, 2.2),
    new THREE.MeshStandardMaterial({
      map: sharedChecker(),
      roughness: 0.7,
      polygonOffset: true,
      polygonOffsetFactor: -3,
      polygonOffsetUnits: -3,
    }),
  );
  finishMesh.position.copy(finish.position).addScaledVector(finish.normal, 0.045);
  finishMesh.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(finish.binormal, finish.tangent, finish.normal));
  group.add(finishMesh);

  const medals = medalsForDistance(length, options.laps, options.medalPace);

  return {
    id: options.id,
    object: group,
    blockers,
    frames,
    lapLength: length,
    width: options.width,
    surfaceId: options.surfaceId,
    spawn: {
      position: spawnSample.position.clone().addScaledVector(spawnSample.normal, CART.rideHeight),
      quaternion: spawnSample.quaternion.clone(),
    },
    checkpoints,
    medals,
    atmosphere: options.atmosphere,
    propulsion: options.propulsion ?? false,
    dust: options.dust ?? false,
    bands: options.bands,
    dispose: () => disposeObject(group),
  };
}

function roadGeometry(frames: SplineFrame[], length: number, width: number): THREE.BufferGeometry {
  return ribbon(frames, length, 0, width, 0.0, true);
}

function ribbon(
  frames: SplineFrame[],
  length: number,
  lateral: number,
  width: number,
  lift: number,
  asphaltUv = false,
): THREE.BufferGeometry {
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const half = width * 0.5;
  const left = new THREE.Vector3();
  const right = new THREE.Vector3();

  frames.forEach((frame, index) => {
    left.copy(frame.position).addScaledVector(frame.binormal, lateral - half).addScaledVector(frame.normal, lift);
    right.copy(frame.position).addScaledVector(frame.binormal, lateral + half).addScaledVector(frame.normal, lift);
    positions.push(left.x, left.y, left.z, right.x, right.y, right.z);
    normals.push(frame.normal.x, frame.normal.y, frame.normal.z, frame.normal.x, frame.normal.y, frame.normal.z);
    const v = asphaltUv ? frame.distance * 0.08 : frame.distance * 0.15;
    uvs.push(0, v, asphaltUv ? 2 : 1, v);
    const next = (index + 1) % frames.length;
    const a = index * 2;
    const b = next * 2;
    indices.push(a, b, a + 1, a + 1, b, b + 1);
  });
  void length;

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  return geometry;
}

function dashedRibbon(
  frames: SplineFrame[],
  length: number,
  lateral: number,
  width: number,
  lift: number,
  dash: number,
  gap: number,
): THREE.BufferGeometry {
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const half = width * 0.5;
  const period = dash + gap;
  let vertex = 0;
  for (let i = 0; i < frames.length; i += 1) {
    const frame = frames[i];
    const along = frame.distance % period;
    if (along > dash) continue;
    const nextIndex = (i + 1) % frames.length;
    const next = frames[nextIndex];
    const span = i === frames.length - 1 ? length - frame.distance : next.distance - frame.distance;
    if (span <= 0) continue;
    const push = (f: SplineFrame, side: number) => {
      const p = f.position.clone().addScaledVector(f.binormal, lateral + side * half).addScaledVector(f.normal, lift);
      positions.push(p.x, p.y, p.z);
      normals.push(f.normal.x, f.normal.y, f.normal.z);
      uvs.push(side > 0 ? 1 : 0, f.distance);
    };
    push(frame, -1);
    push(frame, 1);
    push(next, -1);
    push(next, 1);
    indices.push(vertex, vertex + 2, vertex + 1, vertex + 1, vertex + 2, vertex + 3);
    vertex += 4;
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  return geometry;
}

function addNitroPad(
  group: THREE.Group,
  sample: ReturnType<typeof sampleSpline>,
  roadWidth: number,
  pad: 'small' | 'big',
): THREE.Object3D {
  const big = pad === 'big';
  const plateW = big ? Math.min(roadWidth * 0.62, 7.2) : 2.35;
  const plateL = big ? 4.2 : 3.05;
  const materials: THREE.MeshStandardMaterial[] = [];
  const mat = (color: number, intensity: number) => {
    const material = new THREE.MeshStandardMaterial({
      color,
      emissive: color,
      emissiveIntensity: intensity,
      roughness: 0.32,
      metalness: 0.08,
      polygonOffset: true,
      polygonOffsetFactor: -4,
      polygonOffsetUnits: -4,
    });
    materials.push(material);
    return material;
  };
  const root = new THREE.Group();
  root.name = big ? 'nitro-pad-big' : 'nitro-pad';
  root.position.copy(sample.position).addScaledVector(sample.normal, 0.05);
  root.quaternion.copy(sample.quaternion);
  const plate = new THREE.Mesh(
    new THREE.BoxGeometry(plateW, 0.04, plateL),
    mat(big ? 0xffd15a : 0xff7a18, big ? 1.65 : 1.1),
  );
  root.add(plate);
  const chevron = mat(0xfff6d8, big ? 2 : 1.45);
  const armL = new THREE.Mesh(new THREE.BoxGeometry(plateW * 0.28, 0.02, 0.16), chevron);
  armL.position.set(-plateW * 0.12, 0.04, 0.15);
  armL.rotation.y = 0.6;
  const armR = new THREE.Mesh(new THREE.BoxGeometry(plateW * 0.28, 0.02, 0.16), chevron);
  armR.position.set(plateW * 0.12, 0.04, 0.15);
  armR.rotation.y = -0.6;
  root.add(armL, armR);
  root.userData.boostMaterials = materials;
  group.add(root);
  return root;
}

export function surfaceAlong(distance: number, length: number, fallback: SurfaceId, bands?: SurfaceBand[]): SurfaceId {
  if (!bands?.length || length <= 0) return fallback;
  const u = ((distance / length) % 1 + 1) % 1;
  for (const band of bands) {
    const inside = band.start <= band.end
      ? u >= band.start && u < band.end
      : u >= band.start || u < band.end;
    if (inside) return band.surface;
  }
  return fallback;
}

function warnIfPinched(frames: SplineFrame[], width: number): void {
  const limit = width * 0.85;
  for (let i = 0; i < frames.length; i += 8) {
    for (let j = i + 12; j < frames.length; j += 4) {
      const wrap = Math.min(Math.abs(i - j), frames.length - Math.abs(i - j));
      if (wrap < 12) continue;
      if (frames[i].position.distanceTo(frames[j].position) < limit) {
        console.warn(`Pista possivelmente estreita entre amostras ${i} e ${j}`);
        return;
      }
    }
  }
}

export function poseOnTrack(frames: SplineFrame[], lapLength: number, distance: number, lateral: number, ride: number): {
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
} {
  const sample = sampleSpline(frames, distance, lapLength);
  return {
    position: sample.position.clone().addScaledVector(sample.normal, ride).addScaledVector(sample.binormal, lateral),
    quaternion: sample.quaternion.clone(),
  };
}
